import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Select, Button, Spinner, Alert, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type VariantCandidateItem, type VariantCandidateStatus } from '../lib/adminApiClient';
import { DANGER_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';

/**
 * Product editor — variant dedup review (Figma "Variant dedup queue"). Product
 * groups the matcher flagged as the same item. "Confirm" links them into one
 * variant group (non-destructive); "Reject" dismisses the candidate. The
 * destructive row-collapse is a deferred bg-jobs follow-up. Candidate UPC shows
 * masked last-4 only (feedback_never_show_upc).
 */

const PRIMARY_BTN = { backgroundColor: '#15803d', color: '#fff', '&:hover': { backgroundColor: '#126c34' } };

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

function sourcePill(source: string) {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    catalog_selfjoin: { label: 'catalog self-join', bg: '#E2E8F0', fg: '#475569' },
    kroger_search: { label: 'kroger_search', bg: '#DBEAFE', fg: '#1D4ED8' },
  };
  return map[source] ?? { label: source, bg: '#E2E8F0', fg: '#475569' };
}

export default function VariantDedup() {
  const caps = useCapabilities();
  const canView = caps.has('product:read');
  const canEdit = caps.has('product:edit');
  const navigate = useNavigate();

  const [status, setStatus] = useState<VariantCandidateStatus>('pending');
  const [items, setItems] = useState<VariantCandidateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listVariantCandidates({ status });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load variant candidates');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const act = async (id: string, kind: 'confirm' | 'reject') => {
    setBusy(id);
    setError(null);
    try {
      if (kind === 'confirm') await adminApiClient.confirmVariantCandidate(id);
      else await adminApiClient.rejectVariantCandidate(id);
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  if (caps.loading) return <Centered><Spinner color="brand" /></Centered>;
  if (!canView) return <Alert severity="error">You don’t have permission to view this.</Alert>;

  return (
    <Box>
      <Box component="button" onClick={() => navigate('/product-editor')}
        sx={{ border: 'none', background: 'none', cursor: 'pointer', p: 0, mb: 1.5, color: '#15803d', fontWeight: 600, fontSize: '14px' }}>
        ‹ Product editor
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Variant dedup review</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Product groups the matcher flagged as the same item (different size / pack). Confirm to link
        them into one variant group, or reject.
      </Typography>

      {!canEdit && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t confirm or reject.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter by status" label="Status" native size="small" sx={{ width: 200 }}
          value={status} onChange={(_e, v) => setStatus(v as VariantCandidateStatus)}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'needs_review', label: 'Needs review' },
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'all', label: 'All' },
          ]}
        />
      </Box>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}

      {loading ? (
        <Centered><Spinner color="brand" /></Centered>
      ) : items.length === 0 ? (
        <EmptyState icon="listAlt" title="Nothing to review" body="No variant candidates match this filter." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((c) => {
            const sp = sourcePill(c.source);
            return (
              <Box key={c.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, backgroundColor: '#fff', p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
                    {c.sourceProduct?.itemName ?? c.sourceGlpProductId}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {c.sourceProduct?.brand || '—'} · <span style={{ fontFamily: 'monospace' }}>{c.sourceGlpProductId}</span>
                  </Typography>
                  <Pill label={sp.label} fg={sp.fg} bg={sp.bg} />
                </Box>
                <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 2, p: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#9aa3ab', letterSpacing: 0.4 }}>CANDIDATE VARIANT</Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {[c.candidateTitle, c.candidateBrand, c.candidateSize].filter(Boolean).join(' · ') || '—'}
                    {c.candidateUpcLast4 ? ` · UPC ${c.candidateUpcLast4}` : ''}
                  </Typography>
                  {c.notes && <Typography variant="caption" sx={{ color: '#6b7280' }}>{c.notes}</Typography>}
                </Box>
                {canEdit && c.status === 'pending' && (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button ariaLabel="Confirm variant" buttonText={busy === c.id ? 'Working…' : 'Confirm variant'} variant="contained" size="small" disabled={busy === c.id} sx={PRIMARY_BTN} onClick={() => void act(c.id, 'confirm')} />
                    <Button ariaLabel="Reject" buttonText="Reject" variant="outlined" size="small" disabled={busy === c.id} sx={DANGER_BTN} onClick={() => void act(c.id, 'reject')} />
                  </Box>
                )}
                {c.status !== 'pending' && (
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'capitalize' }}>{c.status}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
